// src/Auth.js - Enhanced Auth Component
import React, { useState, useEffect } from 'react';
import api from './apiClient';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton, 
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import Visibility from '@mui/icons-material/Visibility';       // ⬅️ add
import VisibilityOff from '@mui/icons-material/VisibilityOff'; // ⬅️ add
import { useSnackbar } from 'notistack';

const USERNAME_REGEX = /^[a-zA-Z0-9_]+$/;

function Auth({ onLogin }) {
  const { enqueueSnackbar } = useSnackbar();

  const [step, setStep] = useState('password-login');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState(''); // NEW: for username validation message
  const [emailError, setEmailError] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hoverPassword, setHoverPassword] = useState(false);
  const [hoverConfirmPassword, setHoverConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [loginUsername, setLoginUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const greetings = ['Welcome', 'स्वागतम्', 'nɐmɐste'];
  const [greetingIndex, setGreetingIndex] = useState(0);

    useEffect(() => {
    const trimmed = username.trim();

    // 1) Empty → no error
    if (!trimmed) {
      setUsernameError('');
      return;
    }

    // 2) Local format rule
    if (!USERNAME_REGEX.test(trimmed)) {
      setUsernameError(
        'Only letters, numbers and underscores allowed. No spaces or special characters.'
      );
      return; // don’t hit backend if format is wrong
    }

    // 3) Debounced backend check (is username already taken?)
    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/api/check-username', {
          params: { username: trimmed },
        });

        if (cancelled) return;

        if (res.data && typeof res.data.exists !== 'undefined') {
          if (res.data.exists) {
            setUsernameError('This username is already taken');
          } else {
            setUsernameError('');
          }
        } else {
          console.warn('Unexpected /api/check-username response:', res.data);
          // Do nothing – don’t confuse user with wrong error
        }
      } catch (err) {
        console.error('Error validating username', err?.response?.status, err?.response?.data);
        // Network/backend issue – don’t block user
        // setUsernameError(''); // optional, but safe to leave as is
      }
    }, 400); // run 0.4s after user stops typing

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [username]);


  // Rotate greeting
  useEffect(() => {
    const interval = setInterval(() => {
      setGreetingIndex((i) => (i + 1) % greetings.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

    // Handle redirect after Google auth (new Google user)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleSignup = params.get('google_signup');

    if (googleSignup === '1') {
      const emailParam = params.get('email') || '';
      const fnParam = params.get('first_name') || '';
      const lnParam = params.get('last_name') || '';

      setEmail(decodeURIComponent(emailParam));
      setFirstName(decodeURIComponent(fnParam));
      setLastName(decodeURIComponent(lnParam));

      // Go to "Choose username" screen
      setStep('google-username');

      // Clean URL (remove query params from address bar)
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);


  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer((t) => t - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleGoogleLogin = () => {
    window.location.href = `${api.defaults.baseURL}/auth/google`;
  };

  // Generic OTP sender used for both signup and forgot-password
  const handleSendOtp = async (nextStep, isResend = false) => {
  if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
    enqueueSnackbar('Enter a valid email', { variant: 'warning' });
    return;
  }
  setLoading(true);
  try {
    await api.post('/api/send-otp', { email: email.trim() });
    enqueueSnackbar(isResend ? 'OTP resent!' : 'OTP sent!', { variant: 'success' });
    setStep(nextStep);          // 'otp' or 'forgot-otp'
    setResendTimer(60);
  } catch (err) {
    enqueueSnackbar(err.response?.data?.error || 'Failed to send OTP', { variant: 'error' });
  } finally {
    setLoading(false);
  }
};


  // Verify OTP for forgot password
  const handleVerifyForgotOtp = async () => {
    if (otp.length !== 6) {
      enqueueSnackbar('Enter 6-digit OTP', { variant: 'warning' });
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/verify-otp', { email: email.trim(), otp });
      enqueueSnackbar('OTP verified!', { variant: 'success' });
      setStep('reset-password'); // go to password reset screen (no username here)
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Invalid OTP', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Actually reset password (forgot flow)
  const handleResetPassword = async () => {
    if (password.length < 6) {
      enqueueSnackbar('Password must be 6+ characters', { variant: 'warning' });
      return;
    }
    if (password !== confirmPassword) {
      enqueueSnackbar('Passwords do not match', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/reset-password', { email: email.trim(), password });
      enqueueSnackbar('Password reset successfully! Please login.', {
        variant: 'success',
      });
      
      setPassword('');
      setConfirmPassword('');
      setOtp('');
      setStep('password-login');
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to reset password', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP for normal signup flow
  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      enqueueSnackbar('Enter 6-digit OTP', { variant: 'warning' });
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/verify-otp', { email: email.trim(), otp });
      enqueueSnackbar('Email verified!', { variant: 'success' });
      setStep('set-credentials'); // signup credentials screen
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Invalid OTP', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async () => {
    if (!loginUsername.trim() || !password) {
      enqueueSnackbar('Enter username/email and password', { variant: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/api/login', {
        username: loginUsername.trim(),
        password,
      });
      const { id, token } = res.data;
      localStorage.setItem('rememberedUserId', String(id));
      localStorage.setItem('authToken', token);
      enqueueSnackbar('Logged in successfully!', { variant: 'success' });
      onLogin(id);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Invalid credentials', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckEmail = async () => {
  const trimmed = email.trim();

  // Nothing typed – no error
  if (!trimmed) {
    setEmailError('');
    return;
  }

  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmed)) {
    setEmailError('Enter a valid email address');
    return;
  }

  try {
    // Ask backend if email exists (case-insensitive)
    const res = await api.get('/api/check-email', {
      params: { email: trimmed },
    });

    if (res.data && typeof res.data.exists !== 'undefined') {
      if (res.data.exists) {
        // For signup, this is not allowed
        setEmailError('An account with this email already exists. Please login or use Forgot Password.');
      } else {
        setEmailError('');
      }
    } else {
      console.warn('Unexpected /api/check-email response:', res.data);
      setEmailError('');
    }
  } catch (err) {
    console.error('Error validating email', err?.response?.status, err?.response?.data);
    // Don’t block user on network/API issues – just don’t show any inline error
    setEmailError('');
  }
};




const handleCompleteSignup = async () => {
  const trimmedUsername = username.trim();

  // Basic length check
  if (!trimmedUsername || trimmedUsername.length < 3) {
    enqueueSnackbar('Username must be 3+ characters', { variant: 'warning' });
    return;
  }

  // Format check – same rule as live validation
  if (!USERNAME_REGEX.test(trimmedUsername)) {
    enqueueSnackbar(
      'Username can only have letters, numbers, and underscores. No spaces or special characters.',
      { variant: 'warning' }
    );
    return;
  }

  // If live validation already found an issue (taken / invalid), block submit
  if (usernameError) {
    enqueueSnackbar(usernameError, { variant: 'warning' });
    return;
  }

  // Extra checks for email-password signup flow
  if (step === 'set-credentials') {
    if (password.length < 6) {
      enqueueSnackbar('Password must be 6+ characters', { variant: 'warning' });
      return;
    }
    if (password !== confirmPassword) {
      enqueueSnackbar('Passwords do not match', { variant: 'warning' });
      return;
    }
    if (!agreeTerms) {
      enqueueSnackbar('Please agree to Terms and Privacy', { variant: 'warning' });
      return;
    }
  }

  setLoading(true);
  try {
    const payload = {
      email: email.trim(),
      username: trimmedUsername,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    };

    if (step === 'set-credentials') {
      payload.password = password;
    }

    const res = await api.post('/api/complete-signup', payload);
    const { id, token } = res.data;

    localStorage.setItem('rememberedUserId', String(id));
    localStorage.setItem('authToken', token);
    enqueueSnackbar('Welcome to Krama1!', { variant: 'success' });

    onLogin(id);

    // Ensure personal workspace exists
    const wsRes = await api.get('/api/workplaces', { params: { user_id: id } });
    if (wsRes.data.length === 0) {
      await api.post('/api/workplaces', { name: 'Personal', owner_id: id });
    }
  } catch (err) {
    enqueueSnackbar(err.response?.data?.error || 'Signup failed', {
      variant: 'error',
    });
  } finally {
    setLoading(false);
  }
};




  // ================== RENDER ==================
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      bgcolor="#f8fafc"
    >
      <Paper
        elevation={12}
        sx={{ p: 5, borderRadius: 4, maxWidth: 440, width: '100%' }}
      >
        <Box textAlign="center" mb={4}>
          <img src="/logo.png" alt="Krama" style={{ height: 70 }} />
          <Typography variant="h4" fontWeight="bold" mt={2}>
            Krama
          </Typography>
          <Typography variant="h6" color="text.secondary" mt={1}>
            {greetings[greetingIndex]}
          </Typography>
        </Box>

        {/* SIGNUP SCREEN */}
        {step === 'signup' && (
          <>
            <Typography variant="h6" mb={2}>
              Sign Up
            </Typography>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign up with Google'}
            </Button>
            <Divider sx={{ my: 3 }}>or</Divider>
            <TextField
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError('');
              }}
              onBlur={handleCheckEmail}
              onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
              error={!!emailError}
              helperText={emailError || ' '}
              fullWidth
              margin="normal"
            />

            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => handleSendOtp('otp', false)}
              disabled={loading}
            >
              Sign up with Email
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => {
                
                setStep('password-login');
              }}
              sx={{ mt: 1 }}
            >
              Already have an account? Login
            </Button>
          </>
        )}

        {/* OTP SCREEN - SIGNUP */}
        {step === 'otp' && (
          <>
            <Typography variant="h6" mb={2}>
              Check your email
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              We sent a 6-digit code to <strong>{email}</strong>
            </Typography>
            <TextField
              label="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              fullWidth
              margin="normal"
              autoFocus
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleVerifyOtp}
              disabled={loading}
            >
              Verify OTP
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => handleSendOtp('otp', true)}
              disabled={loading || resendTimer > 0}
              sx={{ mt: 1 }}
            >
              {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => setStep('signup')}
              sx={{ mt: 1 }}
            >
              Back
            </Button>
          </>
        )}

        {/* FORGOT PASSWORD - ENTER EMAIL */}
        {step === 'forgot-password' && (
          <>
            <Typography variant="h6" mb={2}>
              Reset Password
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Enter your email to receive an OTP for reset.
            </Typography>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              margin="normal"
              autoFocus
            />
            <Button
              variant="contained"
              fullWidth
              onClick={() => handleSendOtp('forgot-otp', false)}
              disabled={loading}
            >
              Send OTP
            </Button>

            <Button
              variant="text"
              fullWidth
              onClick={() => {
                
                setStep('password-login');
              }}
              sx={{ mt: 1 }}
            >
              Back
            </Button>
          </>
        )}

        {/* FORGOT OTP VERIFY */}
        {step === 'forgot-otp' && (
          <>
            <Typography variant="h6" mb={2}>
              Verify OTP
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Enter the OTP sent to <strong>{email}</strong>
            </Typography>
            <TextField
              label="OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              fullWidth
              margin="normal"
              autoFocus
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleVerifyForgotOtp}
              disabled={loading}
            >
              Verify
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => handleSendOtp('otp', true)}
              disabled={loading || resendTimer > 0}
              sx={{ mt: 1 }}
            >
              {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
            </Button>
          </>
        )}

        {/* SET NEW PASSWORD (FORGOT FLOW) */}
        {step === 'reset-password' && (
          <>
            <Typography variant="h6" mb={2}>
              Set New Password
            </Typography>
            <TextField
              label="New Password"
              type={showPassword || hoverPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setHoverPassword(true)}
                      onMouseLeave={() => setHoverPassword(false)}
                      edge="end"
                      size="small"
                      sx={{
                        color: 'primary.main',          // default icon color
                        '&:hover': { color: 'primary.main' }, // on hover – theme primary
                      }}
                    >
                      {showPassword || hoverPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputAdornment-root': {
                  opacity: 0,
                  transition: 'opacity 0.2s',
                },
                '&:hover .MuiInputAdornment-root': {
                  opacity: 1,
                },
                '& .MuiInputBase-input': {
                  paddingRight: '32px',
                },
              }}
            />

            <TextField
              label="Confirm New Password"
              type={showConfirmPassword || hoverConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setHoverConfirmPassword(true)}
                      onMouseLeave={() => setHoverConfirmPassword(false)}
                      edge="end"
                      size="small"
                      sx={{
                        color: 'primary.main',          // default icon color
                        '&:hover': { color: 'primary.main' }, // on hover – theme primary
                      }}
                    >
                      {showConfirmPassword || hoverConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputAdornment-root': {
                  opacity: 0,
                  transition: 'opacity 0.2s',
                },
                '&:hover .MuiInputAdornment-root': {
                  opacity: 1,
                },
                '& .MuiInputBase-input': {
                  paddingRight: '32px',
                },
              }}
            />


            <Button
              variant="contained"
              fullWidth
              onClick={handleResetPassword}
              disabled={loading}
            >
              Reset Password
            </Button>
          </>
        )}

        {/* PASSWORD LOGIN SCREEN */}
        {step === 'password-login' && (
          <>
            <Typography variant="h6" mb={2}>
              Login
            </Typography>
            <TextField
              label="Username or Email"
              value={loginUsername}
              onChange={(e) => setLoginUsername(e.target.value)}
              fullWidth
              margin="normal"
              autoFocus
            />
            <TextField
              label="Password"
              type={showPassword || hoverPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}             // click = toggle permanently
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setHoverPassword(true)}                  // hover = show
                      onMouseLeave={() => setHoverPassword(false)}                 // leave = hide (if not clicked)
                      edge="end"
                      size="small"
                      sx={{
                        color: 'primary.main',          // default icon color
                        '&:hover': { color: 'primary.main' }, // on hover – theme primary
                      }}
                    >
                    
                      {showPassword || hoverPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputAdornment-root': {
                  opacity: 0,
                  transition: 'opacity 0.2s',
                },
                '&:hover .MuiInputAdornment-root': {
                  opacity: 1,
                },
                '& .MuiInputBase-input': {
                  paddingRight: '32px',
                },
              }}
            />


            <Button
              variant="outlined"
              size="large"
              fullWidth
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Login with Google'}
            </Button>

            <Button
              variant="contained"
              fullWidth
              onClick={handlePasswordLogin}
              disabled={loading}
              sx={{ mt: 1 }}
            >
              Login
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => {
                
                setStep('forgot-password');
              }}
              sx={{ mt: 1 }}
            >
              Forgot Password?
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => {
                
                setStep('signup');
              }}
              sx={{ mt: 1 }}
            >
              Don't have an account? Sign up
            </Button>
          </>
        )}

        {/* GOOGLE: CHOOSE USERNAME */}
        {step === 'google-username' && (
          <>
            <Typography variant="h6" mb={2}>
              Choose your username
            </Typography>
            <TextField
              label="Username"
              value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  if (usernameError) setUsernameError('');
                }}
               
               error={Boolean(usernameError)}
               helperText={usernameError || ' '}
               fullWidth
               margin="normal"
               autoFocus
            />
            <TextField
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              margin="normal"
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleCompleteSignup}
              disabled={loading}
            >
              Complete Setup
            </Button>
          </>
        )}

        {/* EMAIL: SET USERNAME + PASSWORD (SIGNUP) */}
        {step === 'set-credentials' && (
          <>
            <Typography variant="h6" mb={2}>
              Create your account
            </Typography>
            <TextField
              label="Username"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (usernameError) setUsernameError('');
              }}
              
              error={Boolean(usernameError)}
              helperText={usernameError || ' '}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Password"
              type={showPassword || hoverPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setHoverPassword(true)}
                      onMouseLeave={() => setHoverPassword(false)}
                      edge="end"
                      size="small"
                      sx={{
                        color: 'primary.main',          // default icon color
                        '&:hover': { color: 'primary.main' }, // on hover – theme primary
                      }}
                    >
                      {showPassword || hoverPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputAdornment-root': {
                  opacity: 0,
                  transition: 'opacity 0.2s',
                },
                '&:hover .MuiInputAdornment-root': {
                  opacity: 1,
                },
                '& .MuiInputBase-input': {
                  paddingRight: '32px',
                },
              }}
            />

            <TextField
              label="Confirm Password"
              type={showConfirmPassword || hoverConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setHoverConfirmPassword(true)}
                      onMouseLeave={() => setHoverConfirmPassword(false)}
                      edge="end"
                      size="small"
                      sx={{
                        color: 'primary.main',          // default icon color
                        '&:hover': { color: 'primary.main' }, // on hover – theme primary
                      }}
                    >
                      {showConfirmPassword || hoverConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputAdornment-root': {
                  opacity: 0,
                  transition: 'opacity 0.2s',
                },
                '&:hover .MuiInputAdornment-root': {
                  opacity: 1,
                },
                '& .MuiInputBase-input': {
                  paddingRight: '32px',
                },
              }}
            />


            <FormControlLabel
              control={
                <Checkbox
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms &amp; Conditions
                  </a>{' '}
                  and{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                </Typography>
              }
            />

            <Button
              variant="contained"
              fullWidth
              onClick={handleCompleteSignup}
              disabled={loading}
            >
              Create Account
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}

export default Auth;
